import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentService } from '@/services/appointmentService';
import { getRefreshConfig } from '@/config/refresh.config';

export const useAppointments = (filters: Record<string, unknown> = {}) => {
  return useQuery({
    queryKey: ['appointments', filters],
    queryFn: () => appointmentService.list(filters),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    ...getRefreshConfig('critical'),
  });
};

export const useAppointment = (id: string | number | undefined) => {
  return useQuery({
    queryKey: ['appointment', id],
    queryFn: () => appointmentService.get(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

interface AppointmentMutationVariables {
  id: string | number;
  data: { status?: string; [key: string]: unknown };
}

interface PreviousAppointmentsContext {
  previousAppointments: [unknown, unknown][];
}

export const useCreateAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appointmentData: Record<string, unknown>) => appointmentService.create(appointmentData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
};

export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: AppointmentMutationVariables) => appointmentService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'], exact: false });
      await queryClient.cancelQueries({ queryKey: ['admin', 'stats'] });

      const previousAppointments = queryClient.getQueriesData({
        queryKey: ['appointments'],
        exact: false,
      }) as [unknown, unknown][];

      queryClient.setQueriesData(
        { queryKey: ['appointments'], exact: false },
        (oldData: { data?: { id?: string | number; Id?: string | number; [key: string]: unknown }[] } | undefined) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((apt) => {
              const aptId = apt.id ?? apt.Id;
              if (aptId === id) {
                return {
                  ...apt,
                  status: data.status,
                  Status: data.status,
                };
              }
              return apt;
            }),
          };
        }
      );

      return { previousAppointments };
    },
    onError: (_err, _variables, context: PreviousAppointmentsContext | undefined) => {
      if (context?.previousAppointments) {
        context.previousAppointments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey as unknown[], data);
        });
      }
    },
    onSuccess: (response: { data?: { status?: string; [key: string]: unknown } } | undefined, variables) => {
      const updatedAppointment = response?.data ?? response;

      queryClient.setQueriesData(
        { queryKey: ['appointments'], exact: false },
        (oldData: { data?: { id?: string | number; Id?: string | number; [key: string]: unknown }[] } | undefined) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((apt) => {
              const aptId = apt.id ?? apt.Id;
              if (aptId === variables.id) {
                return {
                  ...apt,
                  ...updatedAppointment,
                  status: updatedAppointment?.status ?? variables.data.status,
                  Status: updatedAppointment?.status ?? variables.data.status,
                };
              }
              return apt;
            }),
          };
        }
      );

      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['appointment', variables.id] });
    },
  });
};

export const useDeleteAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => appointmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
};
